import React from 'react'
import { defaltMapPayload } from 'common/config'
import { IDs, MapStyles } from 'common/consts'
import { BuildingType, Coordinates, MapStyle, MenuType } from 'common/types'
import maplibregl from 'maplibre-gl'
import { LAYERS } from 'common/layers'

type MapContextType = {
  mapbox: maplibregl.Map | null
  setMapbox: React.Dispatch <React.SetStateAction<maplibregl.Map | null>>

  lngLat: maplibregl.LngLat
  setLatLng: React.Dispatch <React.SetStateAction<maplibregl.LngLat>>

  bounds: maplibregl.LngLatBounds
  setBounds: React.Dispatch <React.SetStateAction<maplibregl.LngLatBounds>>

  zoom: number
  setZoom: React.Dispatch <React.SetStateAction<number>>

  intZoom: number
  setIntZoom: React.Dispatch <React.SetStateAction<number>>

  style: MapStyle
  setStyle: React.Dispatch <React.SetStateAction<MapStyle>>

  buildingType: BuildingType
  setBuildingType: React.Dispatch <React.SetStateAction<BuildingType>>

  layerOpacity: {[key in MenuType]: number}
  setLayerOpacity: React.Dispatch <React.SetStateAction<{[key in MenuType]: number}>>

  hasSource: (sourceId: string) => boolean

  hasLayer: (layerId: string) => boolean

  addSource: (sourceId: string, source: any) => void

  addLayer: (layer: any) => void

  removeSource: (sourceId: string) => void

  removeLayer: (layerId: string) => void

  loadImage: (imageId: string, url: string, sdf?: boolean) => void

  addSvg: (imageId: string, size: number, src: string) => void

  updateSourceData: (sourceId: string, data: any) => void

  updateImageSource: (sourceId: string, url: string, coordinates?: Coordinates) => void

  setView: (lng: number, lat: number, zoom?: number) => void

  setLayerFilter: (layerId: string, filter: maplibregl.ExpressionSpecification | null) => void

  setLayoutVisibility: (layerId: string, visibility: boolean) => void

  isLayerVisible: (layerId: string) => boolean
}

export const MapContext = React.createContext<MapContextType | undefined>(undefined)

export const MapProvider = ({children}: React.PropsWithChildren) => {
  const [mapbox, setMapbox] = React.useState<maplibregl.Map | null>(null)
  const [lngLat, setLatLng] = React.useState<maplibregl.LngLat>(defaltMapPayload.center)
  const [bounds, setBounds] = React.useState<maplibregl.LngLatBounds>(defaltMapPayload.bounds)
  const [zoom, setZoom] = React.useState<number>(defaltMapPayload.zoom)
  const [intZoom, setIntZoom] = React.useState<number>(Math.floor(defaltMapPayload.zoom))
  const [style, setStyle] = React.useState<MapStyle>(MapStyles[0])
  const [buildingType, setBuildingType] = React.useState<BuildingType>('None')
  const [layerOpacity, setLayerOpacity] = React.useState<{[key in MenuType]: number}>({
    rain: 0.8,
    'river-floods': 0.5,
    riskline: 0.8,
    dosya: 0.8,
    kouzui: 1.0,
    '': 0.8
  })

  const hasSource = (sourceId: string): boolean => {
    if (!mapbox) return false
    if (mapbox.getSource(sourceId)) {
      return true
    } else {
      return false
    }
  }

  const hasLayer = (layerId: string): boolean => {
    if (!mapbox) return false
    if (mapbox.getLayer(layerId)) {
      return true
    } else {
      return false
    }
  }

  const addSource = (sourceId: string, source: any) => {
    if (!mapbox) return
    if (mapbox.getSource(sourceId)) return
    mapbox.addSource(sourceId, source)
  }

  const addLayer = (layer: any) => {
    if (!mapbox) return
    if (mapbox.getLayer(layer.id)) return

    // レイヤー順番: 観測所 > 建物 > 河川氾濫,雨量,リスクライン,土砂,洪水 > その他
    let beforeLayer = undefined
    const secondLayers = [
      IDs.riverFloods.layer,
      IDs.rainfall.tileLayer,
      IDs.rainfall.wideLayer,
      IDs.riskline.layer,
      IDs.dosya.layer,
      IDs.kouzui.layer,
    ]
    if (layer.id !== LAYERS.OBSERVATORY.id) {
      if (mapbox.getLayer(LAYERS.OBSERVATORY.id)) {
        beforeLayer = LAYERS.OBSERVATORY.id
      }
      if (layer.id !== IDs.composite.buildingLayer && layer.id !== IDs.plateau.layer && !layer.id.startsWith(IDs.roadRegulation.layer)) {
        if (mapbox.getLayer(IDs.composite.buildingLayer)) {
          beforeLayer = IDs.composite.buildingLayer
        } else if (mapbox.getLayer(IDs.plateau.layer)) {
          beforeLayer = IDs.plateau.layer
        }
        if (mapbox.getLayer(`${IDs.roadRegulation.layer}_symbol`)) {
          beforeLayer = `${IDs.roadRegulation.layer}_symbol`
        }
        const isSecondLayer = secondLayers.some(id => layer.id.startsWith(id))
        if (isSecondLayer === false) {
          const layers = mapbox.getStyle().layers || []
          if (layer.id === 'base') {
            beforeLayer = layers[0].id
          } else {
            const groupLayers = layers.filter(layer => secondLayers.some(id => layer.id.startsWith(id)))
            if (groupLayers.length > 0) {
              beforeLayer = groupLayers[0].id
            }
          }
        }
      }
    }
    // console.log('[add]', layer.id, beforeLayer)
    mapbox.addLayer(layer, beforeLayer)
  }

  const removeSource = (sourceId: string) => {
    if (!mapbox) return
    if (mapbox.getSource(sourceId)) {
      mapbox.removeSource(sourceId)
    }
  }

  const removeLayer = (layerId: string) => {
    if (!mapbox) return
    if (mapbox.getLayer(layerId)) {
      // console.log('[remove]', layerId)
      mapbox.removeLayer(layerId)
    }
  }

  const loadImage = (imageId: string, url: string, sdf = true) => {
    if (!mapbox) return
    if (mapbox.hasImage(imageId)) {
      return
    }
    mapbox.loadImage(url)
      .then((image) => {
        if (!mapbox || !image) return
        mapbox.addImage(imageId, image.data, { sdf: sdf })
      })
      .catch((error) => {
        console.error('Error loading image:', error)
      })
  }

  const addSvg = (imageId: string, size: number, svgData: string) => {
    if (!mapbox) return
    if (mapbox.hasImage(imageId)) {
      return
    }
    const img = new Image(size, size)
    img.onload = () => mapbox.addImage(imageId, img, { sdf: false })
    img.src = 'data:image/svg+xml,' + encodeURIComponent(svgData)
  }

  const updateSourceData = (sourceId: string, data: any) => {
    if (!mapbox) return
    const source: any = mapbox.getSource(sourceId)
    if (source) {
      source.setData(data)
    }
  }

  const updateImageSource = (sourceId: string, url: string, coordinates?: Coordinates) => {
    if (!mapbox) return
    const source: any = mapbox.getSource(sourceId)
    if (source) {
      source.updateImage({url: url})
      if (coordinates !== undefined) {
        source.setCoordinates(coordinates)
      }
    }
  }

  const setView = (lng: number, lat: number, zoom?: number) => {
    if (!mapbox) return
    mapbox.setCenter(new maplibregl.LngLat(lng, lat))
    if (zoom) {
      mapbox.setZoom(zoom)
    }
  }

  const setLayerFilter = (layerId: string, filter: maplibregl.ExpressionSpecification | null) => {
    if (!mapbox) return
    if (!mapbox.getLayer(layerId)) return
    mapbox.setFilter(layerId, filter)
  }

  const setLayoutVisibility = (layerId: string, visibility: boolean) => {
    if (!mapbox) return
    if (!mapbox.getLayer(layerId)) return
    mapbox.setLayoutProperty(layerId, 'visibility', visibility ? 'visible' : 'none')
  }

  const isLayerVisible = (layerId: string): boolean => {
    if (!mapbox) return false
    if (!mapbox.getLayer(layerId)) return false
    const visibility = mapbox.getLayoutProperty(layerId, 'visibility')
    return visibility === 'visible'
  }

  const value = React.useMemo(() => ({
    mapbox, setMapbox,
    lngLat, setLatLng,
    bounds, setBounds,
    zoom, setZoom,
    intZoom, setIntZoom,
    style, setStyle,
    buildingType, setBuildingType,
    layerOpacity, setLayerOpacity,
    hasSource,
    hasLayer,
    addSource,
    addLayer,
    removeSource,
    removeLayer,
    loadImage,
    addSvg,
    updateSourceData,
    updateImageSource,
    setView,
    setLayerFilter,
    setLayoutVisibility,
    isLayerVisible,
  }), [
    mapbox, setMapbox,
    lngLat, setLatLng,
    bounds, setBounds,
    zoom, setZoom,
    intZoom, setIntZoom,
    style, setStyle,
    buildingType, setBuildingType,
    layerOpacity, setLayerOpacity,
    hasSource,
    hasLayer,
    addSource,
    addLayer,
    removeSource,
    removeLayer,
    loadImage,
    addSvg,
    updateSourceData,
    updateImageSource,
    setView,
    setLayerFilter,
    setLayoutVisibility,
    isLayerVisible,
  ])

  return (
    <MapContext.Provider value={value}>
      {children}
    </MapContext.Provider>
  )
}
